import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDeprecated } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type ApiResult } from '@app/client';
import { type EidCategories, type EmojiId, type Tag } from '@app/client/common/structs/emoji/emoji';
import config from '@app/config';
import { getReturnLink, newWalletAddressesExist, validPartnerAddresses } from '@app/helpers';
import { PathParams } from '@app/routes/types';
import { getEmojiTags, updateEmojiTag } from '@app/store/actions/emoji';
import { addNotification } from '@app/store/actions/notification';
import { type WalletDataType } from '@app/store/types/partner-integrations';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import { YatV2Loader } from '../../Loader';

import './_YatManageListItem.scss';

interface YatManageListItemProps {
    walletData: WalletDataType | undefined;
    emojiId: EmojiId;
}

export const YatManageListItem = ({ walletData, emojiId }: YatManageListItemProps) => {
    const { t } = useTranslation(['partner_integrations', 'words', 'common', 'translation']);
    const { walletAddresses, appCurrencyTags } = useAppSelector((state) => state.partnerIntegrations);
    const tokens = useAuthTokens((rootState) => rootState.token);
    const setCurrentUserEmoji = useEmojisStore((state) => state.setCurrentEmoji);

    const [existingTags, setExistingTags] = useState<Tag[]>();
    const [allowedTags, setAllowedTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(false);
    const [noNewTags, setNoNewTags] = useState(false);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { [PathParams.PartnerKey]: key } = useParams();

    const handleConnect = useCallback(
        (newInsertTags) => {
            setLoading(true);
            if (!noNewTags && tokens?.refresh_token && newInsertTags) {
                const returnLink = getReturnLink({
                    returnLink: walletData?.return_link,
                    actionType: 'connect',
                    eid: emojiId,
                    refreshToken: tokens.refresh_token,
                });
                const dispatchedPromise = dispatch(
                    updateEmojiTag(emojiId, newInsertTags as unknown as EidCategories[], []) as unknown,
                ) as Promise<ApiResult<EmojiId[]>>;
                dispatchedPromise
                    .then((r) => {
                        if (r.ok) {
                            setCurrentUserEmoji(emojiId);
                            navigate(`/partner/${key}/create-success`, { state: { eid: emojiId, returnLink } });
                        } else {
                            let message = t('common:error_generic');
                            if (r.error && r.error.detail) {
                                message = r.error.detail;
                            }
                            dispatch(addNotification({ message, severity: 'error' }));
                        }
                    })
                    .catch((e) => {
                        dispatch(addNotification({ message: e.message, severity: 'error' }));
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
            return () => {
                setLoading(false);
            };
        },
        [
            noNewTags,
            tokens.refresh_token,
            walletData?.return_link,
            emojiId,
            dispatch,
            setCurrentUserEmoji,
            navigate,
            key,
            t,
        ],
    );

    const getTags = useCallback(() => {
        // get current tags for selected emoji, so we can compare addresses and add them, or send to /dashboard
        getEmojiTags(emojiId, [])
            .then((r) => {
                if (r.ok && r.value && r.value.result) {
                    const existingTags = r.value.result;
                    setExistingTags(existingTags);

                    const newTags = newWalletAddressesExist(
                        walletAddresses as unknown as Tag[],
                        existingTags,
                        appCurrencyTags,
                    );

                    const { newAddressTags, newAddressesOfExistingTags, newAddressesAlreadyAdded } = newTags;
                    const noNewTags =
                        !newAddressTags.length &&
                        !newAddressesOfExistingTags.length &&
                        !newAddressesAlreadyAdded.length;

                    if (newAddressTags.length) {
                        if (newAddressesOfExistingTags.length) {
                            setCurrentUserEmoji(emojiId);
                            navigate(`/partner/${key}/connect-wallet`, { state: { newTags: newTags } });
                        } else {
                            handleConnect(newAddressTags);
                        }
                    }
                    if (!newAddressTags.length && newAddressesAlreadyAdded.length) {
                        dispatch(
                            addNotification({
                                severity: 'alert',
                                message: t('already_connected', { yat: t('translation:yat') }),
                            }),
                        );
                        navigate(config.homeUrl);
                    }

                    if (noNewTags) {
                        setNoNewTags(noNewTags);
                        dispatch(
                            addNotification({
                                message: t('no_new_addresses'),
                                severity: 'alert',
                            }),
                        );
                    }
                }
            })
            .catch((e) => {
                dispatch(addNotification({ message: e.message, severity: 'alert' }));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [emojiId, walletAddresses, appCurrencyTags, setCurrentUserEmoji, navigate, key, handleConnect, dispatch, t]);

    const handleSubmit = useCallback(() => {
        setLoading(true);
        //if no addresses at akk show error
        if (!walletAddresses || noNewTags) {
            setLoading(false);
            dispatch(addNotification({ message: t('no_new_addresses'), severity: 'alert' }));
        }
        //forbidden currencies
        const amount = walletAddresses.length - allowedTags.length;
        if (amount) {
            const message =
                amount === 1
                    ? t('currency_not_supported_copy', {
                          wallet: walletData?.name,
                          yat: t('translation:yat'),
                      })
                    : t('currency_not_supported_plural', {
                          wallet: walletData?.name,
                          yat: t('translation:yat'),
                      });

            dispatch(
                addNotification({
                    title: t('currency_not_supported'),
                    message: message,
                    severity: 'alert',
                }),
            );
        }
        //if there are addresses to be added and no tags yet, get tags
        if (walletAddresses && !existingTags) {
            getTags();
        }
    }, [walletAddresses, getTags, dispatch, t, allowedTags, walletData, noNewTags, existingTags]);

    useEffect(() => {
        if (walletAddresses && appCurrencyTags) {
            const allowed = validPartnerAddresses(walletAddresses as unknown as Tag[], appCurrencyTags);
            setAllowedTags(allowed);
        }
    }, [walletAddresses, appCurrencyTags]);

    return loading ? (
        <YatV2Loader />
    ) : (
        <div className="yat-manage-list-item">
            <div className="yat-manage-list-item__content">
                <div className="yat-manage-list-item__yat">{emojiId}</div>
            </div>
            <ButtonDeprecated color={noNewTags ? 'tertiary' : 'primary'} onClick={handleSubmit} size="small">
                {t('words:select')}
            </ButtonDeprecated>
        </div>
    );
};
